// src/components/customers/CustomerProfileModal.js
import React, { useState, useEffect } from 'react';
import {
    XMarkIcon,
    UserCircleIcon,
    EnvelopeIcon,
    PhoneIcon,
    MapPinIcon,
    GiftIcon,
    ShoppingCartIcon,
    TagIcon,
    CurrencyDollarIcon,
    StarIcon,
    PencilIcon
} from '@heroicons/react/24/outline'; // Added StarIcon for points

const CustomerProfileModal = ({ isOpen, onClose, customer, onSave, availablePrograms }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [formData, setFormData] = useState({});

    useEffect(() => {
        if (customer) {
            // Edit mode: populate form with existing customer data
            setFormData(customer);
            setIsEditing(false); // Start in view mode when editing
        } else {
            // Add mode: initialize form with default empty values
            setFormData({
                name: '',
                email: '',
                phone: '',
                address: '',
                program: availablePrograms[0] || '', // Default to first program
                points: 0,
                tier: 'N/A', // Default tier
                totalPurchases: 0,
                totalSpend: 0,
                lastActivity: new Date().toISOString().slice(0, 10),
                status: 'Active',
                rewardsAvailable: 0,
            });
            setIsEditing(true); // Start directly in edit mode for adding
        }
    }, [customer, isOpen, availablePrograms]); // Re-run when customer or modal state changes

    if (!isOpen) return null;

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
    };

    const handleSave = (e) => {
        e.preventDefault();
        onSave(formData); // Parent component handles whether it's new or existing
        // Modal will close via parent's onClose which is called after onSave
    };

    // Mock activity history for a customer (for display, not part of form data)
    const activityHistory = [
        { id: 1, type: 'Purchase', date: '2025-05-30', description: 'Bought "Premium Coffee Blend", earned 50 points', points: '+50' },
        { id: 2, type: 'Redeem', date: '2025-05-20', description: 'Redeemed $5 discount (100 points)', points: '-100' },
        { id: 3, type: 'Tier Change', date: '2025-05-15', description: 'Achieved Gold Tier status', points: '' },
        { id: 4, type: 'Registration', date: '2025-04-01', description: 'Joined Standard Points Program', points: '+50 welcome' },
    ];

    const modalTitle = customer ? (isEditing ? 'Edit Customer Profile' : 'Customer Profile') : 'Add New Customer';
    const submitButtonText = customer ? 'Save Changes' : 'Add Customer';

    return (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
                {/* Modal Header */}
                <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
                    <h2 className="text-2xl font-bold text-gray-800">{modalTitle}</h2>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
                        <XMarkIcon className="h-6 w-6" />
                    </button>
                </div>

                {/* Modal Body */}
                <form onSubmit={handleSave} className="p-6">
                    <div className="flex items-center space-x-4 mb-6">
                        {customer && customer.avatar ? (
                            <img src={customer.avatar} alt={customer.name} className="h-20 w-20 rounded-full object-cover" />
                        ) : (
                            <UserCircleIcon className="h-20 w-20 text-gray-400" />
                        )}
                        <div>
                            <p className="text-2xl font-bold text-gray-900">
                                {isEditing ? (
                                    <input type="text" name="name" value={formData.name || ''} onChange={handleChange} className="border rounded px-2 py-1 text-2xl font-bold w-full" placeholder="Customer Name" required />
                                ) : (
                                    formData.name
                                )}
                            </p>
                            <p className="text-md text-gray-600">
                                {isEditing ? (
                                    <select name="program" value={formData.program || ''} onChange={handleChange} className="border rounded px-2 py-1 w-full text-sm">
                                        {availablePrograms.map(prog => (
                                            <option key={prog} value={prog}>{prog}</option>
                                        ))}
                                    </select>
                                ) : (
                                    formData.program
                                )}
                            </p>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4 mb-6">
                        {/* Basic Info */}
                        <div className="space-y-3">
                            <h3 className="text-lg font-semibold text-gray-800 mb-2">Contact Information</h3>
                            <div className="flex items-center text-gray-700">
                                <EnvelopeIcon className="h-5 w-5 mr-2 text-gray-500" />
                                {isEditing ? (
                                    <input type="email" name="email" value={formData.email || ''} onChange={handleChange} className="border rounded px-2 py-1 w-full" placeholder="Email Address" required />
                                ) : (
                                    <span>{formData.email}</span>
                                )}
                            </div>
                            <div className="flex items-center text-gray-700">
                                <PhoneIcon className="h-5 w-5 mr-2 text-gray-500" />
                                {isEditing ? (
                                    <input type="text" name="phone" value={formData.phone || ''} onChange={handleChange} className="border rounded px-2 py-1 w-full" placeholder="Phone Number" />
                                ) : (
                                    <span>{formData.phone || 'N/A'}</span>
                                )}
                            </div>
                            <div className="flex items-center text-gray-700">
                                <MapPinIcon className="h-5 w-5 mr-2 text-gray-500" />
                                {isEditing ? (
                                    <textarea name="address" value={formData.address || ''} onChange={handleChange} rows="2" className="border rounded px-2 py-1 w-full" placeholder="Customer Address" />
                                ) : (
                                    <span>{formData.address || 'N/A'}</span>
                                )}
                            </div>
                        </div>

                        {/* Loyalty Summary */}
                        <div className="space-y-3">
                            <h3 className="text-lg font-semibold text-gray-800 mb-2">Loyalty Summary</h3>
                            <div className="flex items-center text-gray-700">
                                <StarIcon className="h-5 w-5 mr-2 text-gray-500" />
                                <span>Points/Tier:
                                    {isEditing ? (
                                        <input type="number" name="points" value={formData.points || 0} onChange={handleChange} className="border rounded px-2 py-1 ml-2 w-24" />
                                    ) : (
                                        <span className="font-bold text-indigo-600 ml-1">
                      {formData.program === 'VIP Tier Rewards' ? formData.tier : `${formData.points?.toLocaleString() || 0} pts`}
                    </span>
                                    )}
                </span>
                            </div>
                            {/* Only show these if not adding a brand new customer or for viewing */}
                            {!customer && isEditing ? null : (
                                <>
                                    <div className="flex items-center text-gray-700">
                                        <GiftIcon className="h-5 w-5 mr-2 text-gray-500" />
                                        <span>Rewards Available: <span className="font-bold text-green-600">{formData.rewardsAvailable?.toLocaleString() || 0}</span></span>
                                    </div>
                                    <div className="flex items-center text-gray-700">
                                        <ShoppingCartIcon className="h-5 w-5 mr-2 text-gray-500" />
                                        <span>Total Purchases: <span className="font-bold">{formData.totalPurchases?.toLocaleString() || 0}</span></span>
                                    </div>
                                    <div className="flex items-center text-gray-700">
                                        <CurrencyDollarIcon className="h-5 w-5 mr-2 text-gray-500" />
                                        <span>Total Spend: <span className="font-bold">${formData.totalSpend?.toLocaleString() || 0}</span></span>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>

                    {/* Activity History - only show in view/edit mode for existing customers */}
                    {customer && (
                        <div className="mt-8">
                            <h3 className="text-lg font-semibold text-gray-800 mb-4">Activity History</h3>
                            <div className="bg-gray-50 p-4 rounded-lg max-h-60 overflow-y-auto">
                                {activityHistory.length === 0 ? (
                                    <p className="text-gray-500 text-center">No recent activity.</p>
                                ) : (
                                    <ul className="space-y-3">
                                        {activityHistory.map((activity) => (
                                            <li key={activity.id} className="flex items-center justify-between text-sm text-gray-700 pb-2 border-b border-gray-200 last:border-b-0">
                                                <div className="flex-1">
                                                    <span className="font-medium mr-2">{activity.type}:</span>
                                                    <span>{activity.description}</span>
                                                </div>
                                                <div className="flex-shrink-0 text-right">
                                                    <span className="text-gray-500 mr-2">{activity.date}</span>
                                                    <span className={`font-semibold ${activity.points.startsWith('+') ? 'text-green-600' : activity.points.startsWith('-') ? 'text-red-600' : 'text-gray-700'}`}>
                            {activity.points}
                          </span>
                                                </div>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Modal Actions */}
                    <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200 mt-6">
                        {!isEditing && (
                            <button
                                type="button"
                                onClick={() => setIsEditing(true)}
                                className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors shadow-sm flex items-center space-x-2"
                            >
                                <PencilIcon className="h-5 w-5" /> <span>Edit Profile</span>
                            </button>
                        )}
                        {isEditing && (
                            <>
                                <button
                                    type="button"
                                    onClick={() => {
                                        if (customer) { // Only reset if it's an existing customer being edited
                                            setFormData(customer);
                                            setIsEditing(false);
                                        } else { // If adding a new customer, just close
                                            onClose();
                                        }
                                    }}
                                    className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors shadow-sm"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="bg-indigo-600 text-white px-6 py-2 rounded-lg hover:bg-indigo-700 transition-colors shadow-md"
                                >
                                    {submitButtonText}
                                </button>
                            </>
                        )}
                        {!isEditing && ( // Only show "Close" if not in editing mode
                            <button
                                type="button"
                                onClick={onClose}
                                className="px-6 py-2 bg-gray-200 rounded-lg text-gray-700 hover:bg-gray-300 transition-colors shadow-sm"
                            >
                                Close
                            </button>
                        )}
                    </div>
                </form>
            </div>
        </div>
    );
};

export default CustomerProfileModal;